import React, { useMemo, useState, useCallback, useEffect } from 'react';
import _get from 'lodash/get';
import { client } from 'cccisd-apollo';
import Loader from 'cccisd-loader';
import Table from 'cccisd-table';
import notification from 'cccisd-notification';
import deploymentsQuery from './deploymentsQuery.graphql';
import progressQuery from './progressQuery.graphql';
import IconReload from 'cccisd-icons/loop3';

const Boilerplate = window.cccisd.boilerplate;

const Progress = ({ sites = [], selectedSiteId, onSiteChange, parentDeployments = [] }) => {
    const [deployments, setDeployments] = useState([]);
    const [selectedDeploymentId, setSelectedDeploymentId] = useState('');
    const [loadingDeployments, setLoadingDeployments] = useState(false);
    const [progressData, setProgressData] = useState([]);
    const [loadingProgress, setLoadingProgress] = useState(false);

    const getParentDeploymentId = useCallback(
        siteId => {
            if (!siteId) {
                return null;
            }

            const Fortress = window.cccisd.fortress;
            const orgId = Fortress.user.acting.group_id;
            const deploymentHandle = `org_${orgId}_site_${siteId}`;
            return parentDeployments.find(d => d.deploymentHandle === deploymentHandle)?.deploymentId || null;
        },
        [parentDeployments]
    );

    const parentDeploymentId = useMemo(
        () => getParentDeploymentId(selectedSiteId),
        [getParentDeploymentId, selectedSiteId]
    );

    const fetchProgress = useCallback(
        async (deploymentId = selectedDeploymentId) => {
            if (!deploymentId || !parentDeploymentId) {
                setProgressData([]);
                return;
            }

            setLoadingProgress(true);
            try {
                const response = await client.query({
                    query: progressQuery,
                    fetchPolicy: 'network-only',
                    variables: {
                        parentDeploymentId: [Number(parentDeploymentId)],
                        childDeploymentId: [Number(deploymentId)],
                    },
                });

                setProgressData(_get(response, 'data.flows.deployment.respondentList', []));
            } catch (error) {
                setProgressData([]);
                notification({
                    type: 'danger',
                    message: 'Unable to load progress for selected timepoint. Please try again.',
                });
            } finally {
                setLoadingProgress(false);
            }
        },
        [parentDeploymentId, selectedDeploymentId]
    );

    useEffect(() => {
        async function fetchDeployments() {
            setSelectedDeploymentId('');
            setProgressData([]);

            if (!parentDeploymentId) {
                setDeployments([]);
                return;
            }

            setLoadingDeployments(true);
            try {
                const response = await client.query({
                    query: deploymentsQuery,
                    fetchPolicy: 'network-only',
                    variables: {
                        parentDeploymentId: [Number(parentDeploymentId)],
                    },
                });

                setDeployments(_get(response, 'data.flows.deploymentList', []));
            } catch (error) {
                setDeployments([]);
                notification({
                    type: 'danger',
                    message: 'Unable to load timepoints for selected site. Please try again.',
                });
            } finally {
                setLoadingDeployments(false);
            }
        }

        fetchDeployments();
    }, [parentDeploymentId]);

    const handleTimepointChange = useCallback(
        async event => {
            const deploymentId = event.target.value;
            setSelectedDeploymentId(deploymentId);
            setProgressData([]);

            if (!deploymentId || !parentDeploymentId) {
                return;
            }

            await fetchProgress(deploymentId);
        },
        [fetchProgress, parentDeploymentId]
    );

    const getSurveyUrl = row => {
        if (!row.deployment || !row.pawn || !row.pawn.pawn || !row.pawn.assignmentProgress.deployment.hash) {
            return null;
        }

        const surveyUrl = `/d/${row.pawn.assignmentProgress.deployment.hash}/${row.pawn.pawn.respondentHash}`;
        return Boilerplate.url(surveyUrl);
    };

    const columns = [
        {
            handle: 'username',
            label: 'Unique Identifier',
            name: 'username',
            sort: true,
            filter: true,
        },
        { handle: 'firstName', label: 'First Name', name: 'firstName', sort: true, filter: true },
        { handle: 'lastName', label: 'Last Name', name: 'lastName', sort: true, filter: true },
        {
            handle: 'startedAt',
            label: 'Started At',
            name: 'startedAt',
            sort: true,
            filter: true,
            filterSettings: {
                type: 'date',
            },
        },
        {
            handle: 'completed',
            label: 'Completed',
            name: 'completed',
            sort: true,
            filter: true,
            filterSettings: {
                type: 'selectbox',
                options: [
                    { label: 'Yes', value: 'Yes' },
                    { label: 'No', value: 'No' },
                ],
            },
        },
        {
            handle: 'print',
            label: 'Print',
            name: 'print',
            sort: false,
            filter: false,
            render: (value, row) => {
                return (
                    <button
                        type="button"
                        className="btn btn-sm btn-warning"
                        onClick={() => window.open(`${row.surveyUrl}/print`, '_blank', 'noopener,noreferrer')}
                        disabled={!row.startedAt}
                    >
                        Print
                    </button>
                );
            },
            renderCSV: (value, row) => {
                return row.surveyUrl;
            },
        },
        {
            handle: 'completedAt',
            label: 'Completed At',
            name: 'completedAt',
            sort: true,
            filter: true,
            filterSettings: {
                type: 'date',
            },
        },
    ];

    const tableData = Array.isArray(progressData)
        ? progressData.map(row => ({
              pawnId: row.pawnId,
              username: _get(row, 'pawn.user.username', ''),
              firstName: _get(row, 'pawn.user.firstName', ''),
              lastName: _get(row, 'pawn.user.lastName', ''),
              startedAt: _get(row, 'pawn.assignmentProgress.startedAt', ''),
              completed: _get(row, 'pawn.assignmentProgress.completed', false) ? 'Yes' : 'No',
              completedAt: _get(row, 'pawn.assignmentProgress.completedAt', ''),
              surveyUrl: getSurveyUrl(row),
          }))
        : [];

    return (
        <>
            <div className="d-flex align-items-end" style={{ gap: '20px', marginBottom: '50px' }}>
                <div className="form-group" style={{ flex: '0 1 320px', maxWidth: '320px', marginBottom: 0 }}>
                    <label htmlFor="progress-site-select">Select Site:</label>
                    <select
                        id="progress-site-select"
                        className="form-select"
                        onChange={event => onSiteChange(event.target.value)}
                        value={selectedSiteId}
                    >
                        <option value="">Select a site</option>
                        {sites.map(site => (
                            <option key={site.group.groupId} value={site.group.groupId}>
                                {site.group.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group" style={{ flex: '0 1 320px', maxWidth: '320px', marginBottom: 0 }}>
                    <label htmlFor="progress-timepoint-select">Select Timepoint:</label>
                    <select
                        id="progress-timepoint-select"
                        className="form-select"
                        value={selectedDeploymentId}
                        onChange={handleTimepointChange}
                        disabled={!selectedSiteId || loadingDeployments}
                    >
                        <option value="">{loadingDeployments ? 'Loading timepoints...' : 'Select a timepoint'}</option>
                        {deployments.map(deployment => (
                            <option key={deployment.deploymentId} value={deployment.deploymentId}>
                                {deployment.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{ flex: '0 0 auto' }}>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => fetchProgress()}
                        disabled={
                            !selectedSiteId ||
                            !parentDeploymentId ||
                            !selectedDeploymentId ||
                            loadingDeployments ||
                            loadingProgress
                        }
                    >
                        <IconReload /> Reload Progress
                    </button>
                </div>
            </div>

            {loadingProgress ? (
                <Loader loading type="inline" />
            ) : (
                <>
                    <Table
                        rowKey="pawnId"
                        name="progress-table"
                        columns={columns}
                        data={tableData}
                        showPerPageOptions
                        perPageOptions={[10, 25, 50, 100]}
                        perPage={10}
                        isCsvDownload
                        csvFilename="progress.csv"
                        csvOmittedColumns={['actions']}
                        noRecordsMessage="No progress data available for the selected timepoint."
                    />
                </>
            )}
        </>
    );
};

export default Progress;
